import { createSelector } from 'reselect'
import Path from 'webmango-utils/path'

import * as Sample from './sample'
import * as Stages from './stages'

const getFileInfo = ({ fileInfo }) => fileInfo

const getNCDump = createSelector([ Sample.getForProcess, Stages.getAll, getFileInfo ], (sample, stages, fileInfo) => {
  if (!stages || !stages.length) return {}
  const inputNode = stages[0].node
  const filepath = Path.resolve(sample.localSamplePath, inputNode.value['inputdata/filename'])
  const info = fileInfo[filepath]

  let ncdump = null
  if (!info || !info.stat || !info.stat.ncdump) {
    ncdump = null
  } else {
    ncdump = info.stat.ncdump
  }

  return ncdump
})

export const getAll = createSelector([ getNCDump, Stages.getAll ], (ncdump, stages) => {
  if (!stages || !stages.length) return {}

  if (!ncdump) return getDefaultDimensions({ stages })

  return getDimensions({ ncdump, stages })
})

export function getOrigin (ncdump) {
  let origin
  if (ncdump.attributes.coordinate_origin_xyz) {
    origin = ncdump.attributes.coordinate_origin_xyz
  }

  return origin && origin.map(Number)
}

export function getSize (ncdump) {
  let size
  if (ncdump.dimensions) {
    // old format
    size = [ncdump.dimensions.x, ncdump.dimensions.y, ncdump.dimensions.z]
  }
  if (ncdump.attributes.total_grid_size_xyz) {
    size = ncdump.attributes.total_grid_size_xyz
  }

  return size && size.map(Number)
}

function getDimensions ({ ncdump, stages }) {
  const base = {
    origin: getOrigin(ncdump) || [0, 0, 0],
    size: getSize(ncdump) || [0, 0, 0],
    stride: [1, 1, 1]
  }

  let currentDimensions = base
  return stages.reduce((dimensions, { node, schema }) => {
    if (schema.name === 'Subset') {
      currentDimensions = subsetDimensions(base, node, currentDimensions)
    } else if (schema.name === 'Resample') {
      currentDimensions = resampleDimensions(base, node, currentDimensions)
    } else if (schema.name === 'Import_Data_File') {
      currentDimensions = importDimensions(node, currentDimensions)
    }

    dimensions[node.id] = currentDimensions
    return dimensions
  }, {})
}

function getDefaultDimensions ({ stages }) {
  return stages.reduce((dimensions, { node, schema }) => {
    return Object.assign(dimensions, {
      [node.id]: schema.name === 'Import_Data_File' ? importDimensions(node) : {
        origin: [0, 0, 0],
        size: [0, 0, 0],
        stride: [1, 1, 1]
      }
    })
  }, {})
}

function getValue (node, key) {
  return node.value[`${node.schemaID}/${key}`]
}

function subsetDimensions (base, node, dimensions) {
  const offset = getValue(node, 'offset').map(Number)
  const size = getValue(node, 'size').map(Number)
    // size of 0 treated as 'same as input'
    .map((val, i) => val || dimensions.size[i])
  const stride = getValue(node, 'stride').map(Number)
  const coordinates = getValue(node, 'coordinates')
  const newDimensions = {
    origin: [...dimensions.origin],
    size: [...dimensions.size],
    stride: [...dimensions.stride]
  }

  for (let i = 0; i < base.size.length; i++) {
    // Check new values
    if (offset[i] + (size[i] - 1) < base.size[i]) {
      if (coordinates === 'relative') {
        newDimensions.origin[i] += offset[i]
        newDimensions.origin[i] /= stride[i]
        newDimensions.size[i] = size[i] / stride[i]
      } else {
        newDimensions.origin[i] = offset[i]
        newDimensions.origin[i] /= stride[i]
        newDimensions.size[i] = size[i] / stride[i]
      }
    } else {
      newDimensions.origin[i] = -1
      newDimensions.size[i] = -1
    }
  }

  return Object.assign(newDimensions, {
    totalVoxels: newDimensions.size.reduce((totalVoxels, size) => totalVoxels * size, 0)
  })
}

function resampleDimensions (base, node, dimensions) {
  const factor = node.value[`${node.schemaID}/resample_grid/FACTOR/resample_grid_FACTOR/resample_factor`].map(Number)
  const usesFactor = node.value[`${node.schemaID}/resample_grid`] === 'FACTOR'

  const newDimensions = {
    origin: [...dimensions.origin],
    size: [...dimensions.size],
    stride: [...dimensions.stride]
  }

  if (!usesFactor) return newDimensions // do nothing if factor not selected

  for (let i = 0; i < base.size.length; i++) {
    newDimensions.size[i] *= factor[i]
    newDimensions.origin[i] *= factor[i]
  }

  return Object.assign(newDimensions, {
    totalVoxels: newDimensions.size.reduce((totalVoxels, size) => totalVoxels * size, 0)
  })
}

function importDimensions (node, dimensions) {
  const size = node.value[`${node.schemaID}/Input_Data_File/size`].map(Number)

  return {
    origin: [0, 0, 0],
    size: size,
    stride: [1, 1, 1],
    totalVoxels: size.reduce((totalVoxels, size) => totalVoxels * size, 0)
  }
}



// WEBPACK FOOTER //
// ../webmango-core/selectors/dimensions.js